import { useState, useEffect, useRef, useCallback } from 'react';
import { X, ChevronLeft, ChevronRight, Heart, MessageCircle, Share2, ZoomIn, ZoomOut } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';

const ImageGalleryViewer = ({ 
  isOpen, 
  onClose, 
  images = [], 
  initialIndex = 0,
  postInfo = null
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isZoomed, setIsZoomed] = useState(false);
  const [showUI, setShowUI] = useState(true);
  const [isClosing, setIsClosing] = useState(false);
  const scrollContainerRef = useRef(null);
  const uiTimeoutRef = useRef(null);
  const isScrollingRef = useRef(false);

  // Reset and scroll to initial index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setIsZoomed(false);
      setShowUI(true);
      setIsClosing(false);
      document.body.style.overflow = 'hidden';
      
      // Scroll to initial image after render
      setTimeout(() => {
        if (scrollContainerRef.current) {
          const scrollWidth = scrollContainerRef.current.clientWidth;
          scrollContainerRef.current.scrollTo({
            left: initialIndex * scrollWidth,
            behavior: 'instant'
          });
        }
      }, 50);
    } else {
      document.body.style.overflow = '';
    }
    
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen, initialIndex]);

  // Auto-hide UI after 3 seconds
  useEffect(() => {
    if (showUI && isOpen) {
      uiTimeoutRef.current = setTimeout(() => {
        setShowUI(false);
      }, 3000);
    }
    return () => clearTimeout(uiTimeoutRef.current);
  }, [showUI, isOpen, currentIndex]);

  // Handle scroll to detect current image
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current || isScrollingRef.current) return;
    
    const container = scrollContainerRef.current;
    const scrollWidth = container.clientWidth;
    const scrollLeft = container.scrollLeft;
    const newIndex = Math.round(scrollLeft / scrollWidth);
    
    if (newIndex !== currentIndex && newIndex >= 0 && newIndex < images.length) {
      setCurrentIndex(newIndex);
      setShowUI(true);
    }
  }, [currentIndex, images.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isOpen) return;
      
      switch (e.key) {
        case 'Escape':
          handleClose();
          break;
        case 'ArrowLeft':
          goToPrevious();
          break;
        case 'ArrowRight':
          goToNext();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentIndex]);

  const scrollToIndex = useCallback((index) => {
    if (!scrollContainerRef.current) return;
    
    isScrollingRef.current = true;
    const scrollWidth = scrollContainerRef.current.clientWidth;
    
    scrollContainerRef.current.scrollTo({
      left: index * scrollWidth,
      behavior: 'smooth'
    });
    
    setCurrentIndex(index);
    setIsZoomed(false);
    setShowUI(true);
    
    // Reset scrolling flag after animation
    setTimeout(() => {
      isScrollingRef.current = false;
    }, 400);
  }, []);

  const goToNext = useCallback(() => {
    if (currentIndex < images.length - 1) {
      scrollToIndex(currentIndex + 1);
    }
  }, [currentIndex, images.length, scrollToIndex]);

  const goToPrevious = useCallback(() => {
    if (currentIndex > 0) {
      scrollToIndex(currentIndex - 1);
    }
  }, [currentIndex, scrollToIndex]);

  const handleClose = useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 200);
  }, [onClose]);

  const handleTap = (e) => {
    if (e.target.closest('button')) return;
    setShowUI(prev => !prev);
    
    // Double tap to zoom
    if (e.detail === 2) {
      setIsZoomed(prev => !prev);
    }
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  // Swipe down to close
  const [touchStartY, setTouchStartY] = useState(null);
  const [swipeDownOffset, setSwipeDownOffset] = useState(0);

  const handleTouchStart = (e) => {
    if (isZoomed) return;
    setTouchStartY(e.touches[0].clientY);
  };

  const handleTouchMove = (e) => {
    if (isZoomed || touchStartY === null) return;
    
    const currentY = e.touches[0].clientY;
    const diff = currentY - touchStartY;
    
    // Only allow downward swipe to close
    if (diff > 0) {
      setSwipeDownOffset(diff);
    }
  };

  const handleTouchEnd = () => {
    if (swipeDownOffset > 100) {
      handleClose();
    }
    setSwipeDownOffset(0);
    setTouchStartY(null);
  };

  if (!isOpen || images.length === 0) return null;

  const currentImage = images[currentIndex];
  const currentPostInfo = currentImage?.postInfo || postInfo;

  return (
    <div 
      className={`fixed inset-0 z-[9999] bg-black flex flex-col transition-all duration-200 ${
        isClosing ? 'opacity-0' : 'opacity-100'
      }`}
      style={{ 
        paddingTop: 'env(safe-area-inset-top)',
        paddingBottom: 'env(safe-area-inset-bottom)',
        transform: swipeDownOffset > 0 ? `translateY(${swipeDownOffset}px)` : 'none',
        opacity: swipeDownOffset > 0 ? Math.max(0, 1 - swipeDownOffset / 300) : 1
      }}
      data-testid="image-gallery-viewer"
    >
      {/* Header */}
      <div 
        className={`absolute top-0 left-0 right-0 z-20 p-4 flex items-center justify-between bg-gradient-to-b from-black/60 to-transparent transition-opacity duration-300 ${
          showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'
        }`}
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 16px)' }}
      >
        <button 
          onClick={handleClose}
          className="w-10 h-10 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-colors active:scale-95"
          data-testid="gallery-close-btn"
        >
          <X className="w-6 h-6" />
        </button>
        
        {images.length > 1 && (
          <div className="px-4 py-2 rounded-full bg-black/50 backdrop-blur-sm text-white text-sm font-semibold">
            {currentIndex + 1} / {images.length}
          </div>
        )}
        
        {/* Spacer to balance layout */}
        <div className="w-10 h-10" />
      </div>

      {/* Scrollable Image Container - Native scroll-snap for smooth swiping */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 flex overflow-x-auto overflow-y-hidden snap-x snap-mandatory scrollbar-hide"
        style={{
          scrollSnapType: 'x mandatory',
          WebkitOverflowScrolling: 'touch',
          scrollBehavior: 'smooth'
        }}
        onScroll={handleScroll}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onClick={handleTap}
      >
        {images.map((image, index) => (
          <div 
            key={index}
            className="flex-shrink-0 w-full h-full flex items-center justify-center snap-center"
            style={{ 
              scrollSnapAlign: 'center',
              scrollSnapStop: 'always'
            }}
          >
            <img 
              src={image.url || image}
              alt={image.caption || `Image ${index + 1}`}
              className={`max-w-full max-h-full object-contain select-none transition-transform duration-300 ${
                isZoomed ? 'scale-150' : ''
              }`}
              draggable={false}
              loading={Math.abs(index - currentIndex) <= 1 ? 'eager' : 'lazy'}
              data-testid={`gallery-image-${index}`}
            />
          </div>
        ))}
      </div>

      {/* Navigation Buttons (Desktop) */}
      {currentIndex > 0 && (
        <button 
          onClick={(e) => { e.stopPropagation(); goToPrevious(); }}
          className={`absolute left-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-all active:scale-95 hidden sm:flex ${
            showUI ? 'opacity-100' : 'opacity-0'
          }`}
          data-testid="gallery-prev-btn"
        >
          <ChevronLeft className="w-8 h-8" />
        </button>
      )}

      {currentIndex < images.length - 1 && (
        <button 
          onClick={(e) => { e.stopPropagation(); goToNext(); }}
          className={`absolute right-4 top-1/2 -translate-y-1/2 z-10 w-12 h-12 rounded-full bg-black/50 backdrop-blur-sm flex items-center justify-center text-white hover:bg-black/70 transition-all active:scale-95 hidden sm:flex ${
            showUI ? 'opacity-100' : 'opacity-0'
          }`}
          data-testid="gallery-next-btn"
        >
          <ChevronRight className="w-8 h-8" />
        </button>
      )}

      {/* Dot Indicators */}
      {images.length > 1 && images.length <= 10 && (
        <div 
          className={`absolute bottom-24 left-0 right-0 flex justify-center gap-2 z-10 transition-opacity duration-300 ${
            showUI ? 'opacity-100' : 'opacity-0'
          }`}
        >
          {images.map((_, idx) => (
            <button
              key={idx}
              onClick={() => scrollToIndex(idx)}
              className={`h-2 rounded-full transition-all duration-300 ${
                idx === currentIndex 
                  ? 'bg-white w-6' 
                  : 'bg-white/40 w-2 hover:bg-white/60'
              }`}
              data-testid={`gallery-dot-${idx}`}
            />
          ))}
        </div>
      )}

      {/* Post Info Footer */}
      {currentPostInfo && (
        <div 
          className={`absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 via-black/50 to-transparent transition-opacity duration-300 z-10 ${
            showUI ? 'opacity-100' : 'opacity-0 pointer-events-none'
          }`}
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 16px)' }}
        >
          {/* Author */}
          <div className="flex items-center gap-3 mb-3">
            <Avatar className="w-10 h-10 border-2 border-white/20">
              <AvatarImage src={currentPostInfo.author?.picture} />
              <AvatarFallback className="bg-[var(--ps-primary)] text-white">
                {currentPostInfo.author?.name?.charAt(0) || 'U'}
              </AvatarFallback>
            </Avatar>
            <div>
              <p className="text-white font-semibold">{currentPostInfo.author?.name || 'Unknown'}</p>
              {currentPostInfo.timestamp && (
                <p className="text-white/60 text-sm">{currentPostInfo.timestamp}</p>
              )}
            </div>
          </div>

          {/* Caption */}
          {currentImage?.caption && (
            <p className="text-white/90 text-sm mb-3 line-clamp-2">{currentImage.caption}</p>
          )}

          {/* Actions */}
          <div className="flex items-center gap-6">
            <button className="flex items-center gap-2 text-white/80 hover:text-white transition-colors active:scale-95">
              <Heart className="w-6 h-6" />
              {currentPostInfo.likes > 0 && <span>{currentPostInfo.likes}</span>}
            </button>
            <button className="flex items-center gap-2 text-white/80 hover:text-white transition-colors active:scale-95">
              <MessageCircle className="w-6 h-6" />
              {currentPostInfo.comments > 0 && <span>{currentPostInfo.comments}</span>}
            </button>
            <button className="flex items-center gap-2 text-white/80 hover:text-white transition-colors active:scale-95">
              <Share2 className="w-6 h-6" />
            </button>
          </div>
        </div>
      )}

      {/* Swipe Hint */}
      {images.length > 1 && currentIndex === 0 && showUI && (
        <div className="absolute bottom-36 left-0 right-0 text-center text-white/40 text-sm z-10">
          <span className="inline-flex items-center gap-2">
            <ChevronLeft className="w-4 h-4 animate-pulse" />
            Swipe to see more
            <ChevronRight className="w-4 h-4 animate-pulse" />
          </span>
        </div>
      )}

      {/* Hide scrollbar */}
      <style>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
    </div>
  );
};

export default ImageGalleryViewer;
